import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export type WizardOptionVariant = 'default' | 'success' | 'warning' | 'danger' | 'info';

export interface WizardOptionProps {
  icon: LucideIcon;
  title: string;
  description?: string;
  onClick: () => void;
  selected?: boolean;
  variant?: WizardOptionVariant;
  disabled?: boolean;
}

const variantStyles: Record<WizardOptionVariant, { bg: string; border: string; hover: string; icon: string; title: string }> = {
  default: {
    bg: 'bg-slate-700/30',
    border: 'border-slate-600/50',
    hover: 'hover:bg-slate-700/50 hover:border-slate-500',
    icon: 'text-slate-300',
    title: 'text-slate-100',
  },
  success: {
    bg: 'bg-green-500/10',
    border: 'border-green-500/30',
    hover: 'hover:bg-green-500/20 hover:border-green-500/50',
    icon: 'text-green-400',
    title: 'text-green-400',
  },
  warning: {
    bg: 'bg-orange-500/10',
    border: 'border-orange-500/30',
    hover: 'hover:bg-orange-500/20 hover:border-orange-500/50',
    icon: 'text-orange-400',
    title: 'text-orange-400',
  },
  danger: {
    bg: 'bg-red-500/10',
    border: 'border-red-500/30',
    hover: 'hover:bg-red-500/20 hover:border-red-500/50',
    icon: 'text-red-400',
    title: 'text-red-400',
  },
  info: {
    bg: 'bg-blue-500/10',
    border: 'border-blue-500/30',
    hover: 'hover:bg-blue-500/20 hover:border-blue-500/50',
    icon: 'text-blue-400',
    title: 'text-blue-400',
  },
};

export function WizardOption({
  icon: Icon,
  title,
  description,
  onClick,
  selected = false,
  variant = 'default',
  disabled = false,
}: WizardOptionProps) {
  const styles = variantStyles[variant];

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'w-full p-4 rounded-xl border text-left transition-all duration-200',
        styles.bg,
        styles.border,
        !disabled && styles.hover,
        selected && 'ring-2 ring-orange-500 ring-offset-2 ring-offset-slate-800',
        disabled && 'opacity-50 cursor-not-allowed'
      )}
    >
      <div className="flex items-center gap-3">
        <Icon className={cn('w-5 h-5 shrink-0', styles.icon)} />
        <div className="flex-1 min-w-0">
          <p className={cn('font-medium', styles.title)}>{title}</p>
          {description && (
            <p className="text-xs text-slate-400 mt-0.5 truncate">{description}</p>
          )}
        </div>
      </div>
    </button>
  );
}
